import ReactGA from 'react-ga4';
import { BadgeTier } from './badges';
import { debugLog, debugError } from './debug';

// Analytics logging level - read from environment variable, default to 2 if not set
const ANALYTICS_LOG_LEVEL = parseInt(process.env.REACT_APP_ANALYTICS_LOG_LEVEL || '2', 10);

interface ModalOpenParams {
  day: number;
  today: boolean;
  best_day_score: number;
  best_day_stars: number;
  total_score: number;
  total_stars: number;
}

interface SubmitResponseParams {
  day: number;
  today: boolean;
  best_day_score: number;
  best_day_stars: number;
  total_score: number;
  total_stars: number;
}

interface ScorePopupParams {
  day: number;
  today: boolean;
  score: number;
  stars: number;
  best_day_score: number;
  best_day_stars: number;
  total_score: number;
  total_stars: number;
  isFeedbackView: boolean;
}

interface BadgeEarnedParams {
  badge_type: string;
  badge_level: BadgeTier;
  total_score: number;
  total_stars: number;
}

interface DayLockedParams {
  day: number;
  total_score: number;
  total_stars: number;
}

interface BadgesPopupParams {
  total_score: number;
  total_stars: number;
  num_total_badges: number;
}

/**
 * Initialize Google Analytics
 * Call this once when the app starts
 */
export const initGA = (): void => {
  const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;

  debugLog(1, '[Analytics] Initializing GA with measurement ID:', measurementId || 'NOT SET');

  if (measurementId) {
    ReactGA.initialize(measurementId, {
      gaOptions: {
        debug_mode: process.env.NODE_ENV === 'development',
      },
    });
    debugLog(1, '[Analytics] Google Analytics initialized with ID:', measurementId);
  } else {
    debugError('Google Analytics not initialized: REACT_APP_GA_MEASUREMENT_ID not found');
  }
};

/**
 * Track a page view
 */
export const trackPageView = (path: string): void => {
  const payload = { hitType: 'pageview', page: path };

  debugLog(ANALYTICS_LOG_LEVEL, '[Analytics] Page View:', payload);

  if (!process.env.REACT_APP_GA_MEASUREMENT_ID) return;

  ReactGA.send(payload);
};

/**
 * Track modal open event
 */
export const trackModalOpen = (params: ModalOpenParams): void => {
  const payload = {
    category: 'Modal',
    action: 'Open Modal',
    label: `Day ${params.day}`,
    ...params,
  };

  debugLog(ANALYTICS_LOG_LEVEL, '[Analytics] Modal Open:', payload);

  if (!process.env.REACT_APP_GA_MEASUREMENT_ID) return;

  ReactGA.event(payload);
};

/**
 * Track response submission event
 */
export const trackSubmitResponse = (params: SubmitResponseParams): void => {
  const payload = {
    category: 'Submission',
    action: 'Submit Response',
    label: `Day ${params.day}`,
    ...params,
  };

  debugLog(ANALYTICS_LOG_LEVEL, '[Analytics] Submit Response:', payload);

  if (!process.env.REACT_APP_GA_MEASUREMENT_ID) return;

  ReactGA.event(payload);
};

/**
 * Track score popup display event
 */
export const trackScorePopup = (params: ScorePopupParams): void => {
  const action = params.isFeedbackView ? 'Feedback Popup' : 'Score Popup';
  const payload = {
    category: 'Score',
    action,
    label: `Day ${params.day} - ${params.stars} stars`,
    ...params,
  };

  debugLog(ANALYTICS_LOG_LEVEL, '[Analytics] Score Popup:', payload);

  if (!process.env.REACT_APP_GA_MEASUREMENT_ID) return;

  ReactGA.event(payload);
};

/**
 * Track badge earned event
 */
export const trackBadgeEarned = (params: BadgeEarnedParams): void => {
  const payload = {
    category: 'Badge',
    action: 'Earn Badge',
    label: `${params.badge_type} - ${params.badge_level}`,
    ...params,
  };

  debugLog(ANALYTICS_LOG_LEVEL, '[Analytics] Badge Earned:', payload);

  if (!process.env.REACT_APP_GA_MEASUREMENT_ID) return;

  ReactGA.event(payload);
};

/**
 * Track day locked popup display event
 */
export const trackDayLocked = (params: DayLockedParams): void => {
  const payload = {
    category: 'Day',
    action: 'Day Locked Popup',
    label: `Day ${params.day}`,
    ...params,
  };

  debugLog(ANALYTICS_LOG_LEVEL, '[Analytics] Day Locked Popup:', payload);

  if (!process.env.REACT_APP_GA_MEASUREMENT_ID) return;

  ReactGA.event(payload);
};

/**
 * Track badges popup display event
 */
export const trackBadgesPopup = (params: BadgesPopupParams): void => {
  const payload = {
    category: 'Badge',
    action: 'Badges Popup',
    label: `${params.num_total_badges} badges`,
    ...params,
  };

  debugLog(ANALYTICS_LOG_LEVEL, '[Analytics] Badges Popup:', payload);

  if (!process.env.REACT_APP_GA_MEASUREMENT_ID) return;

  ReactGA.event(payload);
};
